import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import { InviteRecord } from '../types';

const InviteUsersPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [role, setRole] = useState<'admin' | 'staff' | 'member' | 'robot'>('member');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdLink, setCreatedLink] = useState<string>('');
  const [invites, setInvites] = useState<InviteRecord[]>([]);

  const canInvite = !!user?.permissions?.includes('invite_users');
  const roleOptions = user?.role === 'owner' ? ['admin', 'staff', 'member', 'robot'] : user?.role === 'admin' ? ['staff', 'member', 'robot'] : ['member'];

  const loadInvites = () => {
    authService
      .listInvites()
      .then((response) => setInvites(response.data))
      .catch(() => setError('Failed to load invites'));
  };

  useEffect(() => {
    if (canInvite) {
      loadInvites();
    }
  }, [canInvite]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setCreatedLink('');

    try {
      const response = await authService.createInvite(role);
      setCreatedLink(response.data.invite.link);
      loadInvites();
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to create invite');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canInvite) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="page-shell">
      <div className="container">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Invite onboarding</p>
            <h1 className="page-heading">Generate onboarding links</h1>
            <p className="page-copy">Owners can invite admins, staff, and members. Admins can invite staff and members. Anyone can still register directly as a member.</p>
          </div>
        </div>

        <div className="card">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit} className="compose-form">
            <div className="form-group">
              <label className="form-label" htmlFor="invite-role">Invite role</label>
              <select id="invite-role" className="form-input compact-input" value={role} onChange={(event) => setRole(event.target.value as 'admin' | 'staff' | 'member' | 'robot')}>
                {roleOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </div>
            <div className="action-row">
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Creating...' : 'Create invite link'}</button>
            </div>
          </form>

          {createdLink && (
            <div className="invite-result">
              <p className="detail-subheading">Newest invite link</p>
              <input className="form-input" readOnly value={createdLink} onFocus={(event) => event.currentTarget.select()} />
            </div>
          )}
        </div>

        <div className="card users-table-card">
          <h2 className="section-heading">Recent invites</h2>
          <table className="users-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Token</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.token}>
                  <td><span className={`role-pill role-${invite.role}`}>{invite.role}</span></td>
                  <td className="token-cell">{invite.token}</td>
                  <td>{new Date(invite.created_at).toLocaleString()}</td>
                  <td>{new Date(invite.expires_at).toLocaleString()}</td>
                  <td>{invite.used_at ? 'used' : 'open'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InviteUsersPage;
